import { useEffect, useRef, useState } from 'react';
import _ from 'lodash';
import { SelectInput } from 'src/latest/src/components';
import { getPracticeAdminUsersV1 } from '../../../../../api/user';
import AppButton from '../../../../../components/base/Button/Button';
import { assignScheduler } from '../../../../../redux/services/loggingServices';
import { useSelector, useDispatch } from 'react-redux';
import ConfirmationModalContainer from '../../../../ConfirmationModal/ConfirmationModalContainer';
import { clearModalContent } from '../../../../../redux/modules/modalEvent';
import { Logger } from 'src/latest/src/services';

type Props = {
  selectedUserId: string;
  reassigned: (response: any) => void;
};

export function ReassignModal({ selectedUserId, reassigned }: Props) {
  const [options, setOptions] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedScheduler, setSelectedScheduler] = useState<any>(null);
  const { loggedInUser } = useSelector((state: any) => state?.authentication);
  const dispatch = useDispatch();
  const latestInput = useRef('');

  const fetchSchedulers = async (keyword = '') => {
    setLoading(true);
    const { users } = await getPracticeAdminUsersV1({
      limit: 10,
      offset: 0,
      user_type: 'SCHEDULER',
      keyword: keyword || null,
    });

    if (latestInput.current === keyword) {
      setOptions(users || []);
      setLoading(false);
    }
  };

  const debouncedFetch = useRef(
    _.debounce((keyword: string) => fetchSchedulers(keyword), 400),
  ).current;

  useEffect(() => {
    loggedInUser?.user_type?.includes('PRACTICE_ADMIN') && fetchSchedulers();
  }, []);

  const reassignScheduler = async () => {
    setLoading(true);

    try {
      const resp = await assignScheduler(selectedUserId, selectedScheduler?.id);

      reassigned(resp);
      dispatch(clearModalContent());
    } catch (e) {
      Logger.log('error assigning', e);
    }

    setLoading(false);
  };

  const assignToMySelf = async () => {
    setLoading(true);

    try {
      const resp = await assignScheduler(selectedUserId, loggedInUser?.id);

      reassigned(resp);
      dispatch(clearModalContent());
    } catch (e) {
      Logger.log('error assigning', e);
    }

    setLoading(false);
  };

  return (
    <div className="add-note-text-area add-note-modal-text d-flex flex-column justify-content-center reassign-modal-container">
      {loggedInUser?.user_type?.includes('PRACTICE_ADMIN') ? (
        <div>
          <div className="reassign-title">
            Reassigning this user will transfer all current and future TO-DOs to
            a new scheduler.
          </div>
          <div className="d-flex flex-column align-items-center">
            <SelectInput
              placeholder="Select scheduler"
              options={options}
              value={selectedScheduler}
              getOptionLabel={(option: any) => option.full_name || option.email}
              getOptionValue={(option: any) => option.id}
              filterOption={null}
              isLoading={loading}
              isClearable={true}
              menuZIndex={99}
              className="select-scheduler-input"
              noOptionsMessage={() => 'No schedulers found'}
              onChange={(newValue: any) => {
                setSelectedScheduler(newValue);

                if (!newValue) {
                  latestInput.current = '';
                  setOptions([]);
                  fetchSchedulers('');
                }
              }}
              onInputChange={(newInputValue, { action }) => {
                if (action === 'input-change') {
                  latestInput.current = newInputValue;
                  setOptions([]);
                  setLoading(false);
                  debouncedFetch(newInputValue);
                }
              }}
            />
          </div>
          <AppButton
            className="m-5 modal-btn"
            disabled={
              selectedScheduler === null || selectedScheduler === undefined
            }
            label="Confirm"
            variant="primary"
            textWeight="bold"
            textSize="size-14"
            isProcessing={loading}
            onClick={reassignScheduler}
          />
        </div>
      ) : (
        <ConfirmationModalContainer
          clickedYes={assignToMySelf}
          clickedOnNo={() => dispatch(clearModalContent())}
          dynamicText={
            'Reassigning this user to yourself will transfer all current and future TO-DOs to you.'
          }
          variant={'size-16'}
          className="p-5"
        />
      )}
    </div>
  );
}
